import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Icon } from '@/design-system/components/icons/Icon';
import { ModalHeader } from '@/design-system/components/modal';
import { UniNotes } from '@/design-system/components/notes/UniNote';
import { TextBody, TextTitle, H6, TextCaption } from '@/design-system/components/typography';
import { SxProp, View, Image } from 'dripsy';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ModalView } from '@/design-system/components/modal/ModalView';
import { useModalView } from '@/design-system/components/modal/useModalView.hook';
import { AccountManagementStackRoutes, AccountManagementStackScreenProps } from '../../account.navigator';

import { useCardSensitiveDetails } from './hooks/useCard.hook';

export interface CardDetailInfoProps {}
export const CardDetailInfoWebScreen: React.FC<AccountManagementStackScreenProps<AccountManagementStackRoutes.CardDetailInfo>> = ({
  route
}) => {
  const { account, tab, record } = route.params;
  const { useModalProps, closeModal } = useModalView({});
  const { t } = useTranslation('account-management');
  const { topHeader, body, imageSx, textGroup } = sxStyles;

  const { sensitiveDetail } = useCardSensitiveDetails({ cardId: record?.id! });

  return (
    <ModalView useModalProps={useModalProps} minWidth={500}>
      <View>
        <View sx={topHeader}>
          <ModalHeader title={t('card-account-management::glossary.card-info')} closeModal={closeModal} />
        </View>
        <View sx={body}>
          <View sx={{ alignItems: 'center' }}>
            <Image sx={imageSx} source={record?.product?.imageUrl || require('assets/images/worldcard_blank.png')} />
            <View sx={textGroup}>
              <View sx={{ gap: '$2' }}>
                <TextTitle semiBold sx={{ color: '$white' }}>
                  {record?.embossedName}
                </TextTitle>
                <View sx={{ gap: '$2', flexDirection: 'row' }}>
                  <H6 bold sx={{ color: '$white' }}>
                    {(sensitiveDetail && sensitiveDetail?.cardNumber) || `**** **** **** ${record?.last4}`}
                  </H6>
                  <UniIconButton
                    icon="copyWeb"
                    onPress={() => {
                      navigator.clipboard.writeText((sensitiveDetail && sensitiveDetail?.cardNumber) || record?.last4 || '');
                    }}
                  />
                </View>
              </View>
              <View sx={{ flexDirection: 'row', gap: '$5' }}>
                <View sx={{ gap: '$1', width: 125 }}>
                  <TextCaption sx={{ color: '#DCDCDC' }}>{t('card-account-management::glossary.expiry-date')}</TextCaption>
                  <TextBody sx={{ color: '$white' }}>
                    {record?.expiryMonth}/{(record?.expiryYear || '').substring((record?.expiryYear?.length || 0) - 2 || 0)}
                  </TextBody>
                </View>
                <View sx={{ gap: '$1', width: 125 }}>
                  <TextCaption sx={{ color: '#DCDCDC' }}>CVC2</TextCaption>
                  <TextBody sx={{ color: '$white' }}>{sensitiveDetail && sensitiveDetail?.cvc2}</TextBody>
                </View>
              </View>
            </View>
          </View>
          <View sx={{ flex: 1, display: 'grid', gap: '$3', mt: '$10' }}>
            <UniButton
              variant={ButtonVariant.outline1}
              sx={{ width: '100%', height: 44 }}
              label={t('card-account-management::general.button--close')}
              onPress={closeModal}
            />
          </View>
        </View>
      </View>
    </ModalView>
  );
};

const sxStyles: Record<string, SxProp> = {
  topHeader: {
    height: 70,
    justifyContent: 'center'
  },
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  imageSx: {
    width: 400,
    height: 252
  },
  textGroup: {
    position: 'absolute',
    top: 0,
    left: '$10',
    height: 242,
    paddingX: '$4',
    paddingTop: 112,
    paddingBottom: '$5',
    gap: '$5'
  }
};
